import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import { showToast, showFailToast } from 'vant';
import { storage } from '@/utils/storage';
import { isEmpty, isObject } from 'lodash-es';
import CryptoJS from 'crypto-js';
import md5 from 'md5';
import { useAppOs, useLoginDialog } from '@/store';

type CustomConfig = { disabledEnc?: boolean };
type RequestConfig = AxiosRequestConfig & CustomConfig;
export let headersDataList: RequestConfig | null = null;

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
const SIGNKEY = import.meta.env.VITE_SIGN_KEY;
const ENSKEY = import.meta.env.VITE_ENS_KEY;
const IVKEY = import.meta.env.VITE_IVK_EY;

axios.defaults.timeout = 1000 * 60;
axios.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8';
// 创建axios实例
const service = axios.create({
  // 根据不同env设置不同的baseURL
  baseURL: API_BASE_URL,
});

// 解密
export function enc(textStr: string): string {
  if (!textStr) return '';
  const valueText = decodeURIComponent(textStr);
  try {
    const aesKey = 'enc-';
    const index = valueText.indexOf(aesKey);

    let result = '';

    if (index == -1) {
      result = valueText;
    } else {
      result = valueText.substring(index + aesKey.length, valueText.length);
    }

    const key = CryptoJS.enc.Utf8.parse(ENSKEY);
    const iv = CryptoJS.enc.Utf8.parse(IVKEY);
    const decrypt = CryptoJS.AES.decrypt(result, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypt.toString(CryptoJS.enc.Utf8);
  } catch {
    location.href = `${location.origin}/404`;
    throw Error('密钥解析出错');
  }
}

// 加密
export function encData(value: string): string {
  const key = CryptoJS.enc.Utf8.parse(ENSKEY);
  const iv = CryptoJS.enc.Utf8.parse(IVKEY);
  const ciphertext = CryptoJS.AES.encrypt(value, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const DataBase64 = ciphertext.toString(); //Base64.encode(ciphertext.toString());
  return 'enc-' + DataBase64;
}

// axios实例拦截请求
service.interceptors.request.use(
  (config: RequestConfig) => {
    const timestamps = new Date().getTime();
    let sign = `timestamps=${timestamps}&signKey=${SIGNKEY}`;
    // 排序拼接并做md5加密
    const hasSignObj = {
      ...config.data,
    };
    let newHasSignResult = '';
    const hasSignArr = Object.keys(hasSignObj).sort();
    const regCt = new RegExp('application/x-www-form-urlencoded');
    const regCt1 = new RegExp('application/json');
    const re =
      /select|update|delete|truncate|join|union|exec|insert|drop|count|<html|<script|alert/i;

    // console.log(config.data);

    // 不使用加密
    if (!config.disabledEnc) {
      /* 数据加密 */
      if (regCt.test(config.headers?.['Content-Type'] as string)) {
        /* form-data */
        let newHasSignArr = '';
        hasSignArr.map((i) => {
          if ((hasSignObj[i] || hasSignObj[i] == 0) && !re.test(hasSignObj[i])) {
            newHasSignArr += i + '=' + hasSignObj[i] + '&';
          }
        });
        newHasSignResult = `${newHasSignArr}${sign}`;
        config.transformRequest = [
          function (data) {
            // 将数据转换为表单数据
            let ret = '';
            for (const it in data) {
              ret += encodeURIComponent(it) + '=' + encodeURIComponent(data[it]) + '&';
            }
            return ret;
          },
        ];
      } else if (regCt1.test(config.headers?.['Content-Type'] as string)) {
        /* application/json */
        const newObj: {
          [key: string]: string | number | object;
        } = {};
        hasSignArr.map((i) => {
          if ((hasSignObj[i] || hasSignObj[i] == 0) && !re.test(hasSignObj[i])) {
            newObj[i] = hasSignObj[i];
          }
        });
        const newObjSing = JSON.stringify(newObj);
        newHasSignResult = `${isEmpty(newObj) ? '' : newObjSing + '&'}${sign}`;
        const data = {
          ...config.data,
        };

        // if (newObjSing == '{}') {
        //   config.data = encData(newObjSing);
        // }
        if (config.url?.includes('/h5/userControl/userDetailFeign')) {
          // 该接口即使是空对象也设置data
          config.data = encData(newObjSing);
        } else {
          // 其他接口保持原有逻辑
          if (!isEmpty(data)) {
            config.data = encData(newObjSing);
          }
        }
      }
      sign = md5(newHasSignResult).toUpperCase();
    }

    config.headers = {
      ...config.headers,
      // 当前系统，1：iOS 2：Android 3：H5 4：微信小程序
      osVersion: storage.getItem('osVersion', 2) || 3,
      os: 'Web',
      platform: storage.getItem('platform', 2) || 'H5',
      apiVersion: storage.getItem('apiVersion', 2) || '1.0.0',
      channelType: storage.getItem('channel-type', 2) || '',
      channelId: storage.getItem('channelId', 2) || '',
      token: storage.getItem('token', 1) || '',
      flag: storage.getItem('flag', 2) || '',
      city: storage.getItem('city', 2)
        ? encodeURI(storage.getItem('city', 2) as string)
        : storage.getItem('citychoose', 2)
        ? encodeURI(storage.getItem('citychoose', 2) as string)
        : '',
      locationCity: storage.getItem('localCity', 2)
        ? encodeURI(storage.getItem('localCity', 2) as string)
        : '',
      source: 16, // 优速融落地页单独添加字段
      sign: sign,
      timestamps: timestamps,
    };
    // console.log('------>请求参数', config.data);
    headersDataList = config;
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

const formDataToObject = (formData: FormData): Record<string, any> => {
  // 定义带索引签名的对象（允许任意 string 键，值为 any 类型）
  const obj: Record<string, any> = {};
  if (formData instanceof FormData) {
    formData.forEach((value, key) => {
      obj[key] = value; // 此时 key 作为 string 索引，符合类型定义
    });
  }
  return obj;
};

// axios实例拦截响应
service.interceptors.response.use(
  // 2xx时触发
  (response: AxiosResponse<string> & { config: CustomConfig }) => {
    let result: {
      code?: string;
      msg?: string;
      data?: string | object;
      success?: boolean;
    } = {};
    if (isObject(response.data)) {
      result = response.data;
    } else {
      result = JSON.parse(enc(response.data));
    }
    // 使用时：
    console.log(
      'response------------',
      response.config.data
        ? response.config.disabledEnc
          ? response.config.data
          : response.config.data instanceof FormData
          ? formDataToObject(response.config.data) // 转为对象打印
          : JSON.parse(enc(response.config.data))
        : null,
      result,
      response.config.url
    );
    // console.log(
    //   'response------------',
    //   response.config.data && response.config.disabledEnc
    //     ? response.config.data
    //     : response.config.data && JSON.parse(enc(response.config.data)),
    //   result,
    //   response.config.url
    // );
    const { code, msg, data } = result;
    const appOsStore = useAppOs();
    switch (`${code}`) {
      case '1000':
      case '2007':
        return result;
      case '2000': // 检查 linkId 失效
        msg && showFailToast({ message: msg, duration: 2500, forbidClick: true });
        return Promise.reject(result);
      case '5000': // 无权限
        if (appOsStore.osVer !== 'h5') {
          appOsStore.tokenExpire();
        } else {
          if (
            !(
              response.config.url == '/h5/buriedPointControl/user/visit' ||
              response.config.url == '/h5/buriedPointControl/user/operation'
            )
          ) {
            // sessionStorage.clear();
            // localStorage.clear();
            showToast({
              message: '登录过期，请先登录',
              duration: 2500,
              forbidClick: true,
            });
          }
          // eslint-disable-next-line no-case-declarations
          const loginDialogProps = useLoginDialog();

          if (loginDialogProps.dialogState === '') {
            localStorage.removeItem('token');
            loginDialogProps.checkIsLogin();
            loginDialogProps.addEvent('afterLogin', () => window.location.replace(location.href));
            loginDialogProps.addEvent('cancelLogin', () =>
              showToast({
                message: '刷新页面可再次登录',
                duration: 3000,
                forbidClick: true,
              })
            );
          }
          // 是否token失效
          loginDialogProps.setIsTokenTab(true);
          // if (!routerList.includes(window.location.pathname)) {
          //   window.location.href = '/login';
          // }
        }

        return Promise.reject(result);
      default:
        if (
          msg &&
          !(
            response.config.url == '/h5/buriedPointControl/user/visit' ||
            response.config.url == '/h5/buriedPointControl/user/operation'
          )
        ) {
          showFailToast({ message: msg, duration: 2500, forbidClick: true });
        }

        return Promise.reject(
          response.config.url && response.config.url.includes('reportDownload')
            ? (response as any)
            : result
        );
    }
  },
  // 非2xx时触发
  (error: AxiosError) => {
    showFailToast({ message: '网络异常', duration: 2500, forbidClick: true });
    return Promise.reject(error);
  }
);

export type { AxiosResponse, AxiosRequestConfig, RequestConfig };

export default service;
